import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Play, Square, Send, Loader2, SkipForward } from 'lucide-react';
import type { BuildPlan } from '../../types';

interface StepExecutionPanelProps {
  buildPlan: BuildPlan;
  autoApprove: boolean;
  onAutoApproveChange: (value: boolean) => void;
  onStopGeneration: () => void;
  onSendFeedback: (message: string) => void;
  onAdvanceStep: () => Promise<void>;
  isProcessing: boolean;
}

export function StepExecutionPanel({
  buildPlan,
  autoApprove,
  onAutoApproveChange,
  onStopGeneration,
  onSendFeedback,
  onAdvanceStep,
  isProcessing,
}: StepExecutionPanelProps) {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasGraph = !!buildPlan.graph;
  const currentIndex = buildPlan.currentStepIndex;
  const totalSteps = buildPlan.steps.length;
  const currentStep =
    currentIndex >= 0 && currentIndex < totalSteps ? buildPlan.steps[currentIndex] : null;

  // Graph-aware: use graph node count for progress
  const graphVerifiedCount = hasGraph
    ? buildPlan.graph!.nodes.filter((n) => n.status === 'verified').length
    : 0;
  const graphTotalNodes = hasGraph ? buildPlan.graph!.nodes.length : 0;
  const currentGraphNode =
    hasGraph && buildPlan.currentNodeId
      ? buildPlan.graph!.nodes.find((n) => n.id === buildPlan.currentNodeId)
      : null;

  const allStepsVerified = hasGraph
    ? buildPlan.graph!.nodes.every((n) => n.status === 'verified')
    : currentIndex >= totalSteps && buildPlan.steps.every((s) => s.status === 'verified');

  // Edit budget is blocked when editCount exceeds the backend threshold (12)
  const isBudgetBlocked = currentStep != null && (currentStep.editCount ?? 0) > 12;

  // Step review mode: AI is idle, auto is off, not all steps verified, current step not actively building
  const isInReview =
    !isProcessing && !autoApprove && !allStepsVerified && currentStep?.status !== 'building';

  // Find next step title for the "Next Step" button
  const nextStepTitle = (() => {
    if (!hasGraph || !buildPlan.graph) return null;
    const nextNode = buildPlan.graph.nodes.find(
      (n) => n.status !== 'verified' && n.id !== currentStep?.id,
    );
    return nextNode?.title ?? null;
  })();

  // Auto-show feedback input when budget is blocked and AI is not processing
  // (only in auto mode — in step-by-step mode the main prompt input handles this)
  useEffect(() => {
    if (autoApprove && isBudgetBlocked && !isProcessing && !showFeedbackInput) {
      setShowFeedbackInput(true);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [autoApprove, isBudgetBlocked, isProcessing, showFeedbackInput]);

  // Hide the inline feedback textarea when auto-approve is turned off,
  // since the main prompt input handles chat in step-by-step mode.
  useEffect(() => {
    if (!autoApprove && showFeedbackInput) {
      setShowFeedbackInput(false);
    }
  }, [autoApprove, showFeedbackInput]);

  const handleStop = useCallback(() => {
    onAutoApproveChange(false);
    onStopGeneration();
  }, [onStopGeneration, onAutoApproveChange]);

  const handleSendFeedback = useCallback(() => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    onSendFeedback(trimmed);
    setFeedbackText('');
    setShowFeedbackInput(false);
  }, [feedbackText, onSendFeedback]);

  const handleContinue = useCallback(() => {
    onSendFeedback(
      'Continue. If the current step is fully built, verify and advance to the next step.',
    );
  }, [onSendFeedback]);

  const handleNextStep = useCallback(async () => {
    // Mechanically advance the step pointer via the backend endpoint,
    // then tell the AI to build the next step.
    await onAdvanceStep();
    onSendFeedback('Continue to the next step.');
  }, [onAdvanceStep, onSendFeedback]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendFeedback();
      }
    },
    [handleSendFeedback],
  );

  const hasUnfinishedWork = hasGraph
    ? buildPlan.graph!.nodes.some((n) => n.status !== 'verified')
    : buildPlan.steps.some((s) => s.status !== 'verified');

  // When auto-approve is toggled ON and there's unfinished work, resume building.
  const prevAutoApproveRef = useRef(autoApprove);
  useEffect(() => {
    const wasOff = !prevAutoApproveRef.current;
    prevAutoApproveRef.current = autoApprove;
    if (autoApprove && wasOff && !isProcessing && hasUnfinishedWork && !allStepsVerified) {
      handleContinue();
    }
  }, [autoApprove, isProcessing, hasUnfinishedWork, allStepsVerified, handleContinue]);

  // Show continue when AI is idle, there's an unfinished step,
  // and the edit budget hasn't been exhausted (auto mode or no review mode)
  const showContinue =
    !isProcessing &&
    !showFeedbackInput &&
    !isBudgetBlocked &&
    !allStepsVerified &&
    !isInReview &&
    (currentStep?.status === 'building' || (!currentStep && hasUnfinishedWork));

  // Show "Next Step" button in review mode
  const showNextStep = isInReview && !showFeedbackInput && !isBudgetBlocked && hasUnfinishedWork;

  return (
    <div className="border-t bg-muted/20">
      <div className="flex items-center gap-3 px-4 py-2 min-h-[48px]">
        {/* Step info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium flex items-center gap-1.5">
            {isBudgetBlocked ? (
              `Step ${currentIndex + 1}: ${currentStep!.title} — needs your guidance`
            ) : allStepsVerified ? (
              isProcessing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> Finalizing build...
                </>
              ) : (
                'Build complete'
              )
            ) : isInReview ? (
              (() => {
                const justVerifiedNode =
                  hasGraph && buildPlan.currentNodeId
                    ? buildPlan.graph!.nodes.find((n) => n.id === buildPlan.currentNodeId)
                    : null;
                const justVerifiedIndex =
                  hasGraph && justVerifiedNode
                    ? buildPlan.graph!.nodes.indexOf(justVerifiedNode) + 1
                    : currentIndex + 1;
                const total = hasGraph ? graphTotalNodes : totalSteps;
                return `Step ${justVerifiedIndex} of ${total} complete — review the result`;
              })()
            ) : hasGraph && currentGraphNode ? (
              `Node ${graphVerifiedCount + 1} of ${graphTotalNodes}: ${currentGraphNode.title}`
            ) : currentStep ? (
              `Step ${currentIndex + 1} of ${totalSteps}: ${currentStep.title}`
            ) : !currentStep && hasUnfinishedWork ? (
              isProcessing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> Fias AI is
                  thinking...
                </>
              ) : (
                'Ready to build — press Continue to start'
              )
            ) : hasGraph ? (
              `${graphTotalNodes} nodes complete`
            ) : (
              `${totalSteps} steps complete`
            )}
          </p>
        </div>

        {/* Stop button (shown when AI is streaming) */}
        {isProcessing && (
          <Button
            variant="destructive"
            size="icon"
            className="h-7 w-7"
            onClick={handleStop}
            aria-label="Stop generation"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Step/node progress dots */}
        <div className="flex items-center gap-1">
          {(hasGraph ? buildPlan.graph!.nodes : buildPlan.steps).map(
            (item: { id: string; title: string; status: string }) => (
              <div
                key={item.id}
                className={`h-2 w-2 rounded-full transition-colors ${
                  item.status === 'verified'
                    ? 'bg-primary'
                    : item.status === 'building'
                      ? 'ring-2 ring-primary bg-transparent'
                      : 'bg-muted-foreground/30'
                }`}
                title={`${item.title} (${item.status})`}
              />
            ),
          )}
        </div>

        {/* Auto-approve toggle (hidden for single-step plans) */}
        {(hasGraph ? graphTotalNodes : totalSteps) > 1 && (
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="auto-approve"
              checked={autoApprove}
              onCheckedChange={(checked) => onAutoApproveChange(checked === true)}
            />
            <Label htmlFor="auto-approve" className="text-xs cursor-pointer">
              Auto
            </Label>
          </div>
        )}

        {/* Continue button (shown when AI is idle in auto mode and step is unfinished) */}
        {showContinue && (
          <Button size="sm" onClick={handleContinue}>
            <Play className="h-3.5 w-3.5 mr-1" />
            Continue
          </Button>
        )}

        {/* Next Step button (shown in review mode when user is ready to advance) */}
        {showNextStep && (
          <Button size="sm" onClick={handleNextStep}>
            <SkipForward className="h-3.5 w-3.5 mr-1" />
            {nextStepTitle ? `Next: ${nextStepTitle}` : 'Next Step'}
          </Button>
        )}
      </div>

      {/* Status hint */}
      {showContinue && (
        <div className="px-4 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {!currentStep && hasUnfinishedWork ? (
              'Plan approved. Press Continue to start building.'
            ) : (
              <>
                AI paused between tool calls. Press Continue to keep building
                {' or '}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => {
                    setShowFeedbackInput(true);
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }}
                >
                  give feedback
                </button>
                {' to redirect.'}
              </>
            )}
          </p>
        </div>
      )}

      {/* Review mode hint */}
      {showNextStep && (
        <div className="px-4 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground">
            Review the result in the preview. Chat to request changes, or advance to the next step.
          </p>
        </div>
      )}

      {/* Feedback input row (shown after stop) */}
      {showFeedbackInput && !isProcessing && (
        <div className="flex items-end gap-2 px-4 py-2 border-t border-border/50">
          <Textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isBudgetBlocked
                ? 'The AI needs guidance — describe what to try next...'
                : 'Describe a correction...'
            }
            className="min-h-[40px] max-h-[120px] resize-none text-sm"
            rows={1}
          />
          <Button
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={handleSendFeedback}
            disabled={!feedbackText.trim()}
            aria-label="Send feedback"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
